import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    ViewEncapsulation,
    effect,
    input,
    Renderer2,
} from '@angular/core';
import type { TextStyleCategory, TextStyleSize, TextStyleVariant, TextStyleColor } from './text-style.types';

/**
 * Angular directive for applying typography text styles to elements.
 * The CSS is bundled with the directive, so no separate import is needed.
 *
 * @deprecated Since ADR 0001. This runtime directive instantiates a component
 * view + effect per element and bundles the whole class matrix per use. Prefer
 * the zero-runtime typography utility classes delivered once via
 * `@use '@sirius/typography';` in your global stylesheet:
 *
 * - static:  `class="sir-text-style-paragraph-m-default"`
 *            (or `host: { class: '...' }` for host-level)
 * - dynamic: `[class]="textStyleClass('title', size())"` via the `textStyleClass`
 *            / `textStyleColorClass` helpers from `@sirius/typography`
 * - SCSS escape hatch (pseudo-elements / non-class targets):
 *            `@use '@sirius/typography/mixins' as typo; @include typo.text-style(...)`
 *
 * Scheduled for removal in the next major, coordinated with the
 * `universal-robots/frontend` migration.
 *
 * @example
 * ```html
 * <p siriusTextStyle="paragraph" textStyleSize="m">Regular paragraph text</p>
 * <p siriusTextStyle="paragraph" textStyleSize="m" textStyleVariant="bold">Bold paragraph text</p>
 * <h1 siriusTextStyle="title" textStyleSize="xl">Large title</h1>
 * <span siriusTextStyle="system" textStyleSize="s" textStyleColor="secondary">Label</span>
 * ```
 */
@Component({
    standalone: true,
    selector: '[siriusTextStyle]',
    template: '<ng-content></ng-content>',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['../styles/index.scss'],
    host: {
        '[style]': 'null',
    },
})
export class TextStyleComponent {
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly renderer = inject(Renderer2);

    /**
     * Text style category. Determines which typography style to apply.
     */
    readonly siriusTextStyle = input<TextStyleCategory | undefined>();

    /**
     * Text size. Defaults to 'm'.
     * Available sizes vary by category:
     * - button: m, l
     * - display: s, m, l
     * - link: s, m, l
     * - paragraph: s, m, l
     * - quote: s, m, l
     * - system: s, m, l
     * - title: xs, s, m, l, xl
     */
    readonly textStyleSize = input<TextStyleSize>('m');

    /**
     * Text variant. Defaults to 'default'.
     * Only 'paragraph' and 'system' categories support variants.
     * When using 'default' variant, nested `<b>` and `<strong>` elements
     * will automatically use the bold variant.
     */
    readonly textStyleVariant = input<TextStyleVariant>('default');

    /**
     * Optional color override. When set, applies a color utility class that
     * overrides the per-category color. When unset, each category keeps its
     * own semantic color (e.g. quote uses $sir-color-font-quote).
     */
    readonly textStyleColor = input<TextStyleColor | undefined>(undefined);

    /** @deprecated Use `textStyleSize` instead. Will be removed in a future major version. */
    readonly size = input<TextStyleSize | undefined>(undefined);

    /** @deprecated Use `textStyleVariant` instead. Will be removed in a future major version. */
    readonly variant = input<TextStyleVariant | undefined>(undefined);

    /** @deprecated Use `textStyleColor` instead. Will be removed in a future major version. */
    readonly color = input<TextStyleColor | undefined>(undefined);

    private readonly resolvedSize = computed(() =>
        this.resolveDeprecated(this.size, this.textStyleSize, 'size', 'textStyleSize')
    );

    private readonly resolvedVariant = computed(() =>
        this.resolveDeprecated(this.variant, this.textStyleVariant, 'variant', 'textStyleVariant')
    );

    private readonly resolvedColor = computed(() => {
        const deprecated = this.color();
        if (deprecated !== undefined) {
            this.warnDeprecated('color', 'textStyleColor');
            return deprecated;
        }
        return this.textStyleColor();
    });

    private previousClasses: string[] = [];
    private readonly deprecationWarnings = new Set<string>();

    constructor() {
        effect(() => this.updateClasses());
    }

    private updateClasses(): void {
        const element = this.elementRef.nativeElement;

        this.previousClasses.forEach((cls) => this.renderer.removeClass(element, cls));

        const category = this.siriusTextStyle();
        if (!category) {
            this.previousClasses = [];
            return;
        }

        const newClasses: string[] = [`sir-text-style-${category}-${this.resolvedSize()}-${this.resolvedVariant()}`];

        const color = this.resolvedColor();
        if (color) {
            newClasses.push(`sir-text-style-color-${color}`);
        }

        newClasses.forEach((cls) => this.renderer.addClass(element, cls));
        this.previousClasses = newClasses;
    }

    private resolveDeprecated<T>(
        deprecated: () => T | undefined,
        primary: () => T,
        oldName: string,
        newName: string
    ): T {
        const val = deprecated();
        if (val !== undefined) {
            this.warnDeprecated(oldName, newName);
            return val;
        }
        return primary();
    }

    private warnDeprecated(oldName: string, newName: string): void {
        if (this.deprecationWarnings.has(oldName)) return;
        this.deprecationWarnings.add(oldName);
        console.warn(`[TextStyleComponent] "${oldName}" is deprecated. Use "${newName}" instead.`);
    }
}
